import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Address } from '../../../../model/Address';
import { AddressService } from '../../../service-data/address-service';
import { StartupService } from '../../../service-ui/startup.service';
import { COUNTRIES } from '../../../util/countries';
import { US_STATES } from '../../../util/us-states';

/**
 * User-settings surface for managing the shipping address book.
 *
 * Mirrors the address-book mechanism inside `CartPageComponent` /
 * `cart-page.component.html` (the "Delivery address" accordion), but as a
 * standalone management UI so the user does not need to put items in the
 * cart and pretend to check out in order to add, edit, remove, or change
 * the default address.
 *
 * The same `AddressService` backs both surfaces; entries added or removed
 * here flow through `NotificationsService` and update the live cart-page
 * `addresses` array as well.
 */
@Component({
  selector: 'app-address-book',
  templateUrl: './address-book.component.html',
  styleUrls: ['./address-book.component.css']
})
export class AddressBookComponent implements OnInit, OnDestroy {
  readonly countries = COUNTRIES;
  readonly usStates = US_STATES;

  addresses: Address[] = [];

  /** When non-null, the address currently being edited (a clone, so cancel works). */
  editingAddress: Address = null;
  /** True while editing a brand-new address (not yet saved). */
  editingIsNew = false;

  private onDestroySubject: Subject<boolean> = new Subject();

  constructor(public addressService: AddressService, public startupService: StartupService) {}

  ngOnInit() {
    this.refreshFromService();

    // Live-data subscriptions so adds/updates/deletes from anywhere
    // (other tabs, the cart-page, server-side actions) flow into the list.
    this.addressService.getAddNotifier(false).pipe(takeUntil(this.onDestroySubject))
      .subscribe(() => this.refreshFromService());
    this.addressService.getUpdateNotifier().pipe(takeUntil(this.onDestroySubject))
      .subscribe(() => this.refreshFromService());
    this.addressService.getDeleteNotifier().pipe(takeUntil(this.onDestroySubject))
      .subscribe(() => this.refreshFromService());
  }

  ngOnDestroy() {
    this.onDestroySubject.next(true);
    this.onDestroySubject.unsubscribe();
  }

  private refreshFromService() {
    this.addresses = (this.addressService.objects || []).slice();
  }

  /** Open the inline form to create a new address. */
  startAddNew() {
    const blank = new Address(undefined);
    blank.org_ID = Number(this.startupService.selectedRoleId);
    blank.country = 'US';
    this.editingAddress = blank;
    this.editingIsNew = true;
  }

  /** Open the inline form to edit an existing saved address. */
  startEdit(address: Address) {
    // Clone so the user can cancel without mutating the persisted object.
    this.editingAddress = new Address(address);
    this.editingAddress.ID = address.ID;
    this.editingIsNew = false;
  }

  cancelEdit() {
    this.editingAddress = null;
    this.editingIsNew = false;
  }

  /** Persist the in-progress address. Adds a new row or updates the existing one. */
  saveEdit() {
    if (!this.editingAddress) { return; }
    const addr = this.editingAddress;
    if (addr.ID && addr.ID > 0) {
      this.addressService.updateItem(addr).subscribe(() => {
        this.cancelEdit();
      });
    } else {
      addr.org_ID = Number(this.startupService.selectedRoleId);
      this.addressService.addItem(addr).subscribe(saved => {
        if (saved) {
          this.cancelEdit();
        }
      });
    }
  }

  /** Soft-delete an address from the address book. */
  deleteAddress(address: Address) {
    if (!address || !(address.ID > 0)) { return; }
    this.addressService.deleteItem(address).subscribe();
  }

  /**
   * Mark the given address as the user's default. Mirrors the cart-page
   * `makeAddressDefault` flow: clear any previous default, then PUT the
   * newly-default row.
   */
  makeDefault(address: Address) {
    if (!address || !(address.ID > 0)) { return; }
    for (const existing of (this.addressService.objects || [])) {
      if (existing.default && existing.ID !== address.ID) {
        existing.default = false;
        this.addressService.updateItem(existing).subscribe();
      }
    }
    address.default = true;
    this.addressService.updateItem(address).subscribe();
  }

  /** Whether the in-progress address has the minimum fields needed to save. */
  isEditingComplete(): boolean {
    const a = this.editingAddress;
    if (!a) { return false; }
    return !!(a.firstname && a.lastname && a.address1 && a.city && a.state && a.postalcode && a.country && a.email);
  }
}
